/**
 * CTA Banner Carousel
 * Handles auto-slide, swipe gestures, and pause on hover functionality
 */

class CTABannerCarousel {
  constructor(banner) {
    this.banner = banner;
    this.track = banner.querySelector('[data-carousel-track]');
    this.slides = Array.from(banner.querySelectorAll('.banner__carousel-slide'));
    this.currentIndex = 0;
    
    // Configuration from data attributes
    this.autoSlideSpeed = parseInt(banner.dataset.carouselSpeed) || 5000;
    this.slideEffect = banner.dataset.carouselEffect || 'fade';
    this.pauseOnHover = banner.dataset.pauseOnHover === 'true';
    this.enableSwipe = banner.dataset.enableSwipe === 'true';
    
    // State management
    this.isPlaying = true;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.threshold = 50; // Minimum swipe distance
    
    // Timers
    this.autoSlideTimer = null;
    
    this.init();
  }

  init() {
    if (this.slides.length === 0) {
      return;
    }

    // Always setup accessibility and image loading
    this.setupAccessibility();
    this.setupImageLoading();

    // Make sure first slide is active
    this.slides.forEach((slide, index) => {
      slide.classList.remove('banner__carousel-slide--active');
      if (index === 0) {
        slide.classList.add('banner__carousel-slide--active');
      }
    });

    if (this.slides.length === 1) {
      return;
    }

    // Multiple slides - setup full carousel
    this.setupArrows();
    this.setupAutoSlide();
    this.setupSwipeGestures();
    this.setupHoverPause();
    
    // Start auto-slide
    this.startAutoSlide();
    
    // Handle page visibility changes
    this.setupVisibilityChange();
  }

  setupArrows() {
    this.prevBtn = this.banner.querySelector('[data-carousel-prev]');
    this.nextBtn = this.banner.querySelector('[data-carousel-next]');
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prevSlide());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.nextSlide());
  }

  setupAutoSlide() {
    if (this.autoSlideSpeed <= 0) return;
    
    this.startAutoSlide();
  }

  startAutoSlide() {
    if (!this.isPlaying || this.autoSlideSpeed <= 0) return;
    
    this.stopAutoSlide();
    this.autoSlideTimer = setInterval(() => {
      if (!this.isDragging && this.isPlaying) {
        this.nextSlide();
      }
    }, this.autoSlideSpeed);
  }

  stopAutoSlide() {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
      this.autoSlideTimer = null;
    }
  }

  pauseAutoSlide() {
    this.isPlaying = false;
    this.stopAutoSlide();
  }

  resumeAutoSlide() {
    this.isPlaying = true;
    this.startAutoSlide();
  }

  setupHoverPause() {
    if (!this.pauseOnHover) return;

    this.banner.addEventListener('mouseenter', () => {
      this.pauseAutoSlide();
    });

    this.banner.addEventListener('mouseleave', () => {
      this.resumeAutoSlide();
    });

    // Also pause on focus for accessibility
    this.banner.addEventListener('focusin', () => {
      this.pauseAutoSlide();
    });

    this.banner.addEventListener('focusout', () => {
      this.resumeAutoSlide();
    });
  }

  setupSwipeGestures() {
    if (!this.enableSwipe || !this.track) return;

    // Mouse events for desktop
    this.track.addEventListener('mousedown', this.handleStart.bind(this));
    this.track.addEventListener('mousemove', this.handleMove.bind(this));
    this.track.addEventListener('mouseup', this.handleEnd.bind(this));
    this.track.addEventListener('mouseleave', this.handleEnd.bind(this));

    // Touch events for mobile
    this.track.addEventListener('touchstart', this.handleStart.bind(this), { passive: false });
    this.track.addEventListener('touchmove', this.handleMove.bind(this), { passive: false });
    this.track.addEventListener('touchend', this.handleEnd.bind(this));
    this.track.addEventListener('touchcancel', this.handleEnd.bind(this));

    // Prevent default drag behavior
    this.track.addEventListener('dragstart', (e) => e.preventDefault());
  }

  handleStart(e) {
    if (e.type === 'mousedown' && e.button !== 0) return; // Only left mouse button
    
    this.isDragging = true;
    this.track.classList.add('is-dragging');
    
    const point = e.touches ? e.touches[0] : e;
    this.startX = point.clientX;
    this.currentX = point.clientX;
    
    this.pauseAutoSlide();
  }

  handleMove(e) {
    if (!this.isDragging) return;
    
    e.preventDefault();
    
    const point = e.touches ? e.touches[0] : e;
    this.currentX = point.clientX;
  }

  handleEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.track.classList.remove('is-dragging');
    
    const deltaX = this.currentX - this.startX;
    const absDeltaX = Math.abs(deltaX);
    
    if (absDeltaX > this.threshold) {
      if (deltaX > 0) {
        // Swiped right - go to previous slide
        this.prevSlide();
      } else {
        // Swiped left - go to next slide
        this.nextSlide();
      }
    }
    
    // Resume auto-slide after a delay
    setTimeout(() => {
      if (!this.banner.matches(':hover') && !this.banner.matches(':focus-within')) {
        this.resumeAutoSlide();
      }
    }, 100);
  }

  setupAccessibility() {
    // Add ARIA attributes
    this.banner.setAttribute('role', 'region');
    this.banner.setAttribute('aria-label', 'Image carousel');
    
    this.slides.forEach((slide, index) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-label', `Slide ${index + 1} of ${this.slides.length}`);
      slide.setAttribute('aria-hidden', index !== this.currentIndex ? 'true' : 'false');
    });

    // Keyboard navigation
    this.banner.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prevSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.nextSlide();
          break;
        case ' ':
        case 'Spacebar':
          e.preventDefault();
          if (this.isPlaying) {
            this.pauseAutoSlide();
          } else {
            this.resumeAutoSlide();
          }
          break;
      }
    });
  }

  setupImageLoading() {
    this.slides.forEach(slide => {
      const img = slide.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', () => {
          img.classList.add('loaded');
        });
      } else if (img) {
        img.classList.add('loaded');
      }
    });
  }

  setupVisibilityChange() {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAutoSlide();
      } else if (this.isPlaying) {
        this.resumeAutoSlide();
      }
    });
  }

  nextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
    this.goToSlide(prevIndex);
  }

  goToSlide(index) {
    if (index === this.currentIndex || index < 0 || index >= this.slides.length) {
      return;
    }

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    // Update ARIA attributes
    currentSlide.setAttribute('aria-hidden', 'true');
    nextSlide.setAttribute('aria-hidden', 'false');

    if (this.slideEffect === 'slide') {
      this.performSlideTransition(currentSlide, nextSlide, index > this.currentIndex);
    } else {
      this.performFadeTransition(currentSlide, nextSlide);
    }

    this.currentIndex = index;

    // Dispatch custom event
    this.banner.dispatchEvent(new CustomEvent('carousel:change', {
      detail: {
        currentIndex: this.currentIndex,
        currentSlide: nextSlide,
        previousSlide: currentSlide
      }
    }));
  }

  performFadeTransition(currentSlide, nextSlide) {
    // Remove active class from current slide
    currentSlide.classList.remove('banner__carousel-slide--active');
    
    // Add active class to next slide
    nextSlide.classList.add('banner__carousel-slide--active');
  }

  performSlideTransition(currentSlide, nextSlide, isNext) {
    // Setup next slide position
    if (isNext) {
      nextSlide.style.transform = 'translateX(100%)';
      nextSlide.classList.add('banner__carousel-slide--active');
      
      // Trigger transition
      requestAnimationFrame(() => {
        currentSlide.style.transform = 'translateX(-100%)';
        nextSlide.style.transform = 'translateX(0)';
        
        // Cleanup after transition
        setTimeout(() => {
          currentSlide.classList.remove('banner__carousel-slide--active');
          currentSlide.style.transform = '';
          nextSlide.style.transform = '';
        }, 500);
      });
    } else {
      nextSlide.style.transform = 'translateX(-100%)';
      nextSlide.classList.add('banner__carousel-slide--active');
      
      // Trigger transition
      requestAnimationFrame(() => {
        currentSlide.style.transform = 'translateX(100%)';
        nextSlide.style.transform = 'translateX(0)';
        
        // Cleanup after transition
        setTimeout(() => {
          currentSlide.classList.remove('banner__carousel-slide--active');
          currentSlide.style.transform = '';
          nextSlide.style.transform = '';
        }, 500);
      });
    }
  }

  destroy() {
    this.stopAutoSlide();
    
    // Remove event listeners
    // Note: In a production environment, you'd want to store references to bound functions
    // to properly remove event listeners
    
    this.banner = null;
    this.track = null;
    this.slides = null;
  }
}

// Initialize carousels when DOM is ready
function initCTACarousels() {
  const carouselBanners = document.querySelectorAll('.banner--carousel');

  carouselBanners.forEach((banner) => {
    if (banner.ctaCarousel) {
      return;
    }

    const carousel = new CTABannerCarousel(banner);
    banner.ctaCarousel = carousel; // Store reference for later access
  });
}

// Initialize on DOM content loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCTACarousels);
} else {
  initCTACarousels();
}

// Re-initialize when Shopify sections are loaded/reloaded
document.addEventListener('shopify:section:load', initCTACarousels);
document.addEventListener('shopify:section:reorder', initCTACarousels);

// Clean up when section is unloaded
document.addEventListener('shopify:section:unload', (e) => {
  const banner = e.target.querySelector('.banner--carousel');
  if (banner && banner.ctaCarousel) {
    banner.ctaCarousel.destroy();
    delete banner.ctaCarousel;
  }
});

// Export for potential external use
window.CTABannerCarousel = CTABannerCarousel;